import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../../lib/map/whenMapLoaded";
import { attachAttribution, ESRI_CREDITS } from "../../lib/map/attribution";
import "maplibre-gl/dist/maplibre-gl.css";

export default function PlatformOperatingAoiMap({
    aois,
    selectedId,
    loadAoiGeometry,
}) {
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [geomCache, setGeomCache] = useState({});

    // ------------------------------------------------------------------
    // INIT MAP
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!mapContainerRef.current) return;

        const map = new maplibregl.Map({
            container: mapContainerRef.current,
            style: {
                version: 8,
                sources: {
                    "esri-world-imagery": {
                        type: "raster",
                        tiles: [
                            "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        ],
                        tileSize: 256,
                        attribution: ESRI_CREDITS["World_Imagery"],
                    },
                    "esri-boundaries": {
                        type: "raster",
                        tiles: [
                            "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                        ],
                        tileSize: 256,
                        attribution: ESRI_CREDITS["Reference/World_Boundaries_and_Places"],
                    },
                },
                layers: [
                    { id: "imagery", type: "raster", source: "esri-world-imagery" },
                    {
                        id: "boundaries",
                        type: "raster",
                        source: "esri-boundaries",
                        paint: { "raster-opacity": 0.8 },
                    },
                ],
            },
            center: [-102, 32],
            zoom: 6,
            attributionControl: { compact: false },
        });
        // Esri's credit and "Powered by Esri" (REM-684).
        attachAttribution(map);

        map.getCanvas().style.background = "#0C0E10";
        mapRef.current = map;

        whenMapLoaded(map, () => setMapReady(true));

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // No AOI selected → clear map
        if (!selectedId) {
            const sourceId = "operating-aoi";

            if (map.getSource(sourceId)) {
                map.removeLayer("operating-aoi-fill");
                map.removeLayer("operating-aoi-outline");
                map.removeSource(sourceId);
            }

            return;
        }
    }, [selectedId]);

    // ------------------------------------------------------------------
    // RENDER SELECTED AOI
    // ------------------------------------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady || !selectedId) return;

        let cancelled = false;

        async function render() {
            const baseAoi = aois.find(a => a.id === selectedId);
            if (!baseAoi) return;

            // Zoom immediately
            if (Array.isArray(baseAoi.bounds) && baseAoi.bounds.length === 4) {
                map.fitBounds(
                    [
                        [baseAoi.bounds[0], baseAoi.bounds[1]],
                        [baseAoi.bounds[2], baseAoi.bounds[3]],
                    ],
                    { padding: 40, duration: 500 }
                );
            }

            let geom = geomCache[selectedId];

            if (!geom) {
                try {
                    const full = await loadAoiGeometry(selectedId);
                    if (cancelled) return;

                    geom = full.geom;
                    setGeomCache(prev => ({ ...prev, [selectedId]: geom }));
                } catch (e) {
                    console.error("Failed to load operating AOI geometry", e);
                    return;
                }
            }

            const sourceId = "operating-aoi";

            if (!map.getSource(sourceId)) {
                map.addSource(sourceId, {
                    type: "geojson",
                    data: {
                        type: "Feature",
                        geometry: geom,
                        properties: {},
                    },
                });

                map.addLayer({
                    id: "operating-aoi-fill",
                    type: "fill",
                    source: sourceId,
                    paint: {
                        "fill-color": "#1693f9",
                        "fill-opacity": 0.2,
                    },
                });

                map.addLayer({
                    id: "operating-aoi-outline",
                    type: "line",
                    source: sourceId,
                    paint: {
                        "line-color": "#1693f9",
                        "line-width": 2,
                    },
                });
            } else {
                map.getSource(sourceId).setData({
                    type: "Feature",
                    geometry: geom,
                    properties: {},
                });
            }
        }

        render();
        return () => { cancelled = true; };
    }, [selectedId, aois, mapReady]);

    // ------------------------------------------------------------------
    // RENDER
    // ------------------------------------------------------------------
    return (
        <div className="relative w-full h-full bg-bg">
            <div
                ref={mapContainerRef}
                className="absolute inset-0 w-full h-full"
            />
        </div>
    );
}